import { type FunctionComponent, useState } from 'react'
import { Grid, GridItem, Link, TypographyV2 } from '@which/seatbelt'
import { ChevronDownIcon, ChevronUpIcon } from '@which/seatbelt/src/components/Icons/Navigational'
import { dynamicGa4DataLayerPush } from '@which/shared'

import classNames from 'classnames'

import type { Article, Product } from '../../../../../generated/frontend'
import { usePageProps } from '../../../../usePageProps'
import styles from './RecentlyViewedTextList.module.scss'

export type RecentlyViewedItem = Partial<Article> | Partial<Product>

type RecentlyViewedTextListProps = {
  items: RecentlyViewedItem[]
}

const getLink = (item: RecentlyViewedItem, index: number, isPaidMember: boolean) => {
  let title: string | undefined
  let linkHref: string | undefined
  let reviewScore: string | undefined

  if (item.__typename === 'Article') {
    const { short_headline, headline, url } = item
    title = short_headline || headline
    linkHref = url
  } else if (item.__typename === 'Product') {
    const { manufacturer, model, productUrl, testScore } = item
    title = `${manufacturer?.name}, ${model}`
    linkHref = productUrl
    reviewScore = testScore?.overallScore?.toString()
  } else {
    console.warn('Unknown item type in RecentlyViewedTextList:', item)
    return null
  }

  if (!title || !linkHref) {
    console.warn('Missing title or linkHref in RecentlyViewedTextList item:', item)
    return null
  }

  return (
    <Link
      href={linkHref}
      textStyle="sb-text-body-small-regular"
      data-index={(index + 1).toString()}
      data-which-id="recently viewed link"
      data-section="continue reading"
      data-card-name={title}
      data-spec="banner"
      data-personalisation="true"
    >
      {title}
      {isPaidMember && reviewScore && <span>{` (${reviewScore}%)`}</span>}
    </Link>
  )
}

export const RecentlyViewedTextList: FunctionComponent<RecentlyViewedTextListProps> = ({
  items,
}) => {
  const [expandedOnMobile, setExpandedOnMobile] = useState<boolean>(false)
  const { userAccessState, context } = usePageProps()

  const isPaidMember = ['AUTHENTICATED_FULL_ACCESS', 'FULL_ACCESS'].includes(
    userAccessState?.transformTypeDecision ?? ''
  )

  const handleExpandCollapse = () => {
    dynamicGa4DataLayerPush({
      event: 'click_dropdown',
      item_text: expandedOnMobile ? 'collapse' : 'expand',
      action_group: 'recently viewed',
    })
    setExpandedOnMobile(!expandedOnMobile)
  }

  return (
    <div
      className={classNames(styles.recentlyViewedTextListContainer, {
        [styles.expandedOnMobile]: expandedOnMobile,
      })}
      data-testid="recently-viewed-text-list-container"
    >
      <Grid
        className={classNames('w-page-wrapper', styles.recentlyViewedTextList)}
        data-testid="pick-up-where-you-left-off-banner"
      >
        <GridItem
          span={{ medium: 12, large: 12, xlarge: 12 }}
          className={classNames(
            {
              [styles.centeredOnHome]: context === 'home',
            },
            styles.recentlyViewedTextListBox
          )}
        >
          <TypographyV2
            tag="span"
            textStyle="sb-text-body-small-regular"
            className={styles.recentlyViewedTextListTitle}
          >
            Recently viewed:
          </TypographyV2>

          <ul id="recently-viewed-text-list-items" className={styles.recentlyViewedTextListItems}>
            {items.map((item, index) => (
              <li key={index} className={styles.recentlyViewedTextListItem}>
                {getLink(item, index, isPaidMember)}
              </li>
            ))}
          </ul>
          <button
            type="button"
            className={styles.collapseButton}
            onClick={handleExpandCollapse}
            title={
              expandedOnMobile ? 'Collapse recently viewed items' : 'Expand recently viewed items'
            }
            aria-label={
              expandedOnMobile ? 'Collapse recently viewed items' : 'Expand recently viewed items'
            }
            aria-expanded={expandedOnMobile}
            aria-controls="recently-viewed-text-list-items"
          >
            {expandedOnMobile ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </button>
        </GridItem>
      </Grid>
    </div>
  )
}
